import React from 'react'
import {
  Box,
  Container,
  Divider,
  Grid,
  Pagination,
  PaginationItem,
  Typography,
} from '@mui/material'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import Breadcrumbs from '@components/Breadcrumbs'
import { useHelpArticles } from '@api/helpCenter'
import NextLink from '@imports/NextLink'
import useQueryState from '@components/useQueryState'
import ArticleCard from './ArticleCard'

function UnderlineText({ children }) {
  return (
    <Grid container>
      <Grid item>
        {children}
        <Divider sx={{ borderColor: 'primary.main', borderBottomWidth: 3 }} />
      </Grid>
    </Grid>
  )
}

function Categories() {
  const { t } = useTranslation()
  const {
    locale,
    query: { category },
  } = useRouter()

  const enablePagination = !!category
  const [page, setPage] = useQueryState('page', { defaultValue: 1, type: 'integer' })
  React.useEffect(() => {
    if (page !== 1) setPage(1)
  }, [locale, category])

  const helpArticleQuery = useHelpArticles({ locale, category, page })

  // handle paginated structure for filtered data by category (?category=id)
  const helpArticles = category
    ? [{ articles: helpArticleQuery?.data?.data ?? [] }]
    : helpArticleQuery.data

  return (
    <Box py={10}>
      <Container maxWidth="xl">
        {!!category && (
          <Breadcrumbs
            items={[
              { text: t('help-center:home'), url: '/' },
              { text: t('help-center:help_center'), url: '/help-center' },
            ]}
          />
        )}
        <Box
          sx={{ overflow: 'hidden' }}
          pb={2}
        >
          <Grid
            container
            spacing={15}
          >
            {helpArticles?.map(({ category: articleCategory, articles }, index) => (
              <Grid
                key={articleCategory?.id ?? articles?.[0]?.category?.id ?? index}
                item
                xs={12}
              >
                <Grid
                  container
                  spacing={5}
                  alignItems="stretch"
                >
                  <Grid
                    item
                    xs={12}
                  >
                    <UnderlineText>
                      <Typography
                        variant="h4"
                        display="inline"
                      >
                        <NextLink
                          href={`/help-center?category=${
                            articleCategory?.id ?? articles?.[0]?.category?.id
                          }`}
                        >
                          {articleCategory?.title ?? articles?.[0]?.category?.title}
                        </NextLink>
                      </Typography>
                    </UnderlineText>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                  >
                    <Grid
                      container
                      spacing={2}
                    >
                      {articles?.map((article) => (
                        <Grid
                          key={article.id}
                          item
                          xs={12}
                          sm={6}
                          md={4}
                          xl={3}
                        >
                          <ArticleCard article={article} />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            ))}
            {enablePagination && (
              <Grid
                item
                xs={12}
                container
                justifyContent="center"
              >
                <Grid item>
                  <Pagination
                    color="primary"
                    sx={{
                      mt: 5,
                      '& .MuiSvgIcon-root': {
                        transform: 'scale(1) !important',
                      },
                    }}
                    count={helpArticleQuery.data?.meta?.last_page ?? 0}
                    renderItem={(item) => (
                      <PaginationItem
                        sx={{ fontFamily: 'var(--cairo-font) !important' }}
                        {...item}
                      />
                    )}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
        </Box>
      </Container>
    </Box>
  )
}

export default Categories
